import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusPill, type StatusTone } from "@/components/ui/status-pill";

import {
  useCancelInvitation,
  useGroupInvitations,
  useResendInvitation,
} from "../api";
import { apiErrorDetail } from "../lib/errors";
import { GroupRole, type PartnerInvitation } from "../types";

const STATUS_TONE: Record<string, StatusTone> = {
  pending: "warning",
  expired: "destructive",
  declined: "muted",
  licence_cap: "destructive",
};

/** Refused, or closed by a full group: a record of the answer — neither resent
 * nor cancelled; inviting again is the way back. */
const isAnswered = (inv: PartnerInvitation) =>
  inv.status === "declined" || inv.status === "licence_cap";

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

/**
 * The group's unaccepted invitations — pending, expired and refused — read
 * from the server, so they survive a reload. The SPA twin of the « Invitations
 * en attente » table on the Django group page; like it, the card is absent
 * while there is nothing to list.
 */
export function GroupInvitationList({ groupId }: { groupId: string }) {
  const { t } = useTranslation();
  const invitations = useGroupInvitations(groupId);
  const resend = useResendInvitation();
  const cancel = useCancelInvitation();

  if (!invitations.data?.length) return null;

  const onError = (err: unknown) =>
    toast.error(apiErrorDetail(err) ?? t("partners.actionFailed"));

  const status = (inv: PartnerInvitation) => (
    <StatusPill tone={STATUS_TONE[inv.status] ?? "muted"}>
      {t(`partners.groupInvitations.status.${inv.status}`)}
    </StatusPill>
  );

  // A local partner's invitation and a member's sit in the same list.
  const role = (inv: PartnerInvitation) =>
    t(
      inv.assigned_group_role === GroupRole.LocalPartner
        ? "partners.invite.roleLocalPartner"
        : "partners.invite.roleMember",
    );

  const actions = (inv: PartnerInvitation) =>
    isAnswered(inv) ? null : (
      <>
        <Button
          size="sm"
          variant="outline"
          disabled={resend.isPending}
          onClick={() =>
            resend.mutate(inv.id, {
              onSuccess: () => toast.success(t("partners.invite.resent")),
              onError,
            })
          }
        >
          {t("partners.invite.resend")}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={cancel.isPending}
          onClick={() => {
            if (!window.confirm(t("partners.groupInvitations.confirmCancel"))) return;
            cancel.mutate(inv.id, {
              onSuccess: () => toast.success(t("partners.invite.cancelled")),
              onError,
            });
          }}
        >
          {t("partners.invite.cancelInvite")}
        </Button>
      </>
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t("partners.invite.pending")}</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Mobile: one card per invitation; the table squishes on a phone. */}
        <ul className="space-y-2 md:hidden">
          {invitations.data.map((inv) => (
            <li key={inv.id} className="rounded-lg border p-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <span className="min-w-0 break-words font-semibold">{inv.email}</span>
                {status(inv)}
              </div>
              <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 text-xs text-muted-foreground">
                <dt>{t("partners.invite.roleLabel")}</dt>
                <dd>{role(inv)}</dd>
                <dt>{t("partners.groupInvitations.colSent")}</dt>
                <dd>{formatDateTime(inv.created_at)}</dd>
                <dt>{t("partners.groupInvitations.colExpires")}</dt>
                <dd>{formatDateTime(inv.expires_at)}</dd>
              </dl>
              <div className="mt-3 flex flex-wrap items-center gap-2">{actions(inv)}</div>
            </li>
          ))}
        </ul>

        <div className="hidden overflow-x-auto md:block">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th scope="col" className="px-3 py-2 font-semibold">
                  {t("partners.groupInvitations.colEmail")}
                </th>
                <th scope="col" className="px-3 py-2 font-semibold">
                  {t("partners.invite.roleLabel")}
                </th>
                <th scope="col" className="px-3 py-2 font-semibold">
                  {t("partners.groupInvitations.colSent")}
                </th>
                <th scope="col" className="px-3 py-2 font-semibold">
                  {t("partners.groupInvitations.colExpires")}
                </th>
                <th scope="col" className="px-3 py-2 font-semibold">
                  {t("partners.groupInvitations.colStatus")}
                </th>
                <th scope="col" className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {invitations.data.map((inv) => (
                <tr key={inv.id} className="border-b align-top last:border-0">
                  <td className="px-3 py-3 font-semibold">{inv.email}</td>
                  <td className="px-3 py-3">{role(inv)}</td>
                  <td className="px-3 py-3">{formatDateTime(inv.created_at)}</td>
                  <td className="px-3 py-3">{formatDateTime(inv.expires_at)}</td>
                  <td className="px-3 py-3">{status(inv)}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {actions(inv)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
